import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import { CALL_API } from 'redux-api-middleware';
import { ROUTE } from '../../middleware/route';
import { chainAPIActions } from '../../middleware/chain';
import {
  accessStart,
  accessComplete,
  ACCESS_START_FAILURE,
  ACCESS_COMPLETE_RECEIVE,
  ACCESS_COMPLETE_FAILURE,
} from '../auth';
import { validate } from '../../middleware/validator';
import paths from '../../../routes/paths';
import { fetchUser, fetchUserV3, fetchUserMarketingConsent } from '../user';

export const isEmailValid = (email) => email !== '' && email.includes('@');
export const isCodeValid = (code) => /^[0-9]{6}$/.test(code);

// ACTIONS
export const LOGIN_START = 'LOGIN/LOGIN_START';
export const EMAIL_INVALID = 'LOGIN/EMAIL_INVALID';
export const CODE_INVALID = 'LOGIN/CODE_INVALID';
export const SET_LOGIN_EMAIL = 'LOGIN/SET_LOGIN_EMAIL';
export const CLEAR_LOGIN_ERROR = 'LOGIN/CLEAR_LOGIN_ERROR';
export const HIDE_APP_PROMOTION = 'LOGIN/HIDE_APP_PROMOTION';

// ACTION CREATORS
export const setLoginEmail = (email) => ({
  type: SET_LOGIN_EMAIL,
  email,
});

export const clearLoginError = () => ({
  type: CLEAR_LOGIN_ERROR,
});

const accessStartWithRoute = (email) => {
  const success = accessStart(email);
  success[CALL_API].types[1] = {
    type: success[CALL_API].types[1],
    [ROUTE]: paths.login.code,
  };
  return success;
};

const accessResendWithRoute = (email) => {
  const success = accessStart(email);
  success[CALL_API].types[1] = {
    type: success[CALL_API].types[1],
    [ROUTE]: paths.login.resend,
  };
  return success;
};

export const loginStart = (email) => ({
  type: 'BEGIN_LOGIN_START',
  [validate]: {
    validator: () => isEmailValid(email),
    always: { type: LOGIN_START, email },
    success: accessStartWithRoute(email),
    fail: { type: EMAIL_INVALID, email },
  },
});

export const loginResend = (email) => ({
  type: 'BEGIN_LOGIN_START',
  [validate]: {
    validator: () => isEmailValid(email),
    always: { type: LOGIN_START, email },
    success: accessResendWithRoute(email),
    fail: { type: EMAIL_INVALID, email },
  },
});

export const loginComplete = (rnd, email, completeAction = accessComplete) => ({
  type: 'BEGIN_LOGIN_START',
  [validate]: {
    validator: () => isCodeValid(rnd),
    success: chainAPIActions(
      completeAction(rnd, email),
      () => fetchUserV3(),
      () => fetchUserMarketingConsent(),
      () => fetchUser()
    ),
    fail: { type: CODE_INVALID },
  },
});

export const hideAppPromotion = () => ({
  type: HIDE_APP_PROMOTION,
});

// SELECTORS
export const getEmail = (state) => state.email;
export const getError = (state) => state.error;
export const getEmailValidationMessage = (state) => state.emailInvalidMessage;
export const getCodeValidationMessage = (state) => state.codeInvalidMessage;
export const getShowAppPromotion = (state) => state.showAppPromotion;

// REDUCERS
export const email = handleActions(
  {
    [LOGIN_START]: (state, action) => action.email,
    [SET_LOGIN_EMAIL]: (state, action) => action.email,
    [ACCESS_COMPLETE_RECEIVE]: () => null,
  },
  null
);

const error = handleActions(
  {
    [LOGIN_START]: () => null,
    [ACCESS_START_FAILURE]: () => 'API call failed',
    [ACCESS_COMPLETE_FAILURE]: () =>
      'It was not possible to sign you in. Please request another sign in link and try again.',
    [CLEAR_LOGIN_ERROR]: () => null,
  },
  null
);

const emailInvalidMessage = handleActions(
  {
    [LOGIN_START]: () => null,
    [EMAIL_INVALID]: () => 'Enter a valid email address',
  },
  null
);

const codeInvalidMessage = handleActions(
  {
    [LOGIN_START]: () => null,
    [CODE_INVALID]: () => 'Enter a valid 6 digit code',
    [ACCESS_COMPLETE_FAILURE]: () =>
      'Use the code in the most recent email from us and make sure you’ve entered it correctly. You can use the ‘Resend email’ button to get a new code',
  },
  null
);

const showAppPromotion = handleActions(
  {
    [HIDE_APP_PROMOTION]: () => false,
  },
  true
);

export default combineReducers({
  error,
  email,
  emailInvalidMessage,
  codeInvalidMessage,
  showAppPromotion,
});
